import { TemplatePortal } from '@angular/cdk/portal';
import { Injectable, inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import {
	KeyValuesPair,
	LookupDto,
	LookupType,
	OptionsDto,
	StartupCustomisationDto,
} from '@siza-staff-care/shared/data';
import { SessionStateCleanupStore } from '@siza-staff-care/shared/session-state-cleanup';
import { Observable, Subject, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { AccessLevel, SecurityMap, StartupParams } from './app-state-types';
import { FlatMenuItem } from './flat-menu-item';
import { RawSecurityMap, buildFlattenedSecurityMap } from './flatten-security-map';
import { localisation } from './localisation';
import { SearchAndReplace, Settings } from './settings';

type LookupRegistry = Record<string, LookupDto[]>;

type LookupsRegistration = { typeName: string; entries: LookupDto[] }[];

type ButtonsToolbarContentConfig = {
	portal: TemplatePortal;
	positionLeft?: boolean;
	host?: string;
};

type AuthenticationData = {
	jwt: string;
	securityMap?: SecurityMap;
	flatMenuItems?: FlatMenuItem[];
	editorMap?: EditorMap;
	options?: OptionsDto;
};

export type EditorFlags = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;
export type EditorMap = Record<string, EditorFlags>;

export type SecurityConfigStackEntry = {
	editorMap?: EditorMap;
	securityMap?: SecurityMap;
	flattenedSecurityMap?: Record<string, AccessLevel>;
};

type SessionStateStorable = {
	jwt?: string;
	lastAccessTime?: Date;
	messageCount?: number;
	alertCount?: number;
	menuItems?: KeyValuesPair[];
	editorMap?: EditorMap;
	securityMap?: SecurityMap;
	flattenedSecurityMap?: Record<string, AccessLevel>;
	flatMenuItems?: FlatMenuItem[];
	startupParams?: StartupParams;
	settings?: Settings;
	lookups?: LookupRegistry;
	securityConfigStack?: SecurityConfigStackEntry[];
	hash?: string;
};

type ScreenToolbarConfig = {
	portal: TemplatePortal | undefined;
	screenName: string;
	noSpacer?: boolean;
};

type ToolbarConfig = {
	portal: TemplatePortal | undefined;
	host?: string;
	noSpacer?: boolean;
};

type ScreenNameConfig = {
	screenName: string;
	host?: string;
};

type IsPaintingSecurityParams = {
	isPaintingSecurity: boolean;
	isPaintingSecurityForStaff: boolean;
};

interface AppState {
	storable?: SessionStateStorable;
	screenToolbarConfigByHost?: Record<string, ScreenToolbarConfig>;
	topHost?: string;
	toolbarButtonsPortals?: ButtonsToolbarContentConfig[];
	sideNavOpen?: boolean;
	allowSideNavAutoOpen?: boolean;
	isHandset?: boolean;
	isCoarseTouchDevice?: boolean;
	redirectAfterLogonUrl?: string;
	collapseToolbarButtons?: boolean;
	isPaintingSecurity?: boolean;
	isPaintingSecurityForStaff?: boolean;
	isPaintingWorkflowSecurity?: boolean;
}

const defaultState: AppState = {
	allowSideNavAutoOpen: true,
};

interface LocalState {
	defaultUsername?: string | null;
	customisation?: StartupCustomisationDto;
}

const SessionStorage_Key = 'Session';
const LocalStorage_Key = 'Local';

@Injectable({
	providedIn: 'root',
})
export class AppStateService extends ComponentStore<AppState> {
	static instance: AppStateService;

	private sessionStateCleanup = inject(SessionStateCleanupStore);

	private localState?: LocalState;
	private _securityRouteOverride?: string;

	private rebuildMenusSubject = new Subject<void>();
	rebuildMenus$ = this.rebuildMenusSubject.asObservable();

	private readonly screenToolbarConfigByHost$ = this.select(
		({ screenToolbarConfigByHost }) => screenToolbarConfigByHost || {},
	);
	readonly storable$ = this.select(({ storable }) => storable);
	readonly jwt$ = this.select(this.storable$, (storable) => storable?.jwt);
	readonly messageCount$ = this.select(this.storable$, (storable) => storable?.messageCount);
	readonly alertCount$ = this.select(this.storable$, (storable) => storable?.alertCount);
	readonly securityMap$ = this.select(this.storable$, (storable) => storable?.securityMap);
	readonly editorMap$ = this.select(this.storable$, (storable) => storable?.editorMap);
	readonly flattenedSecurityMap$ = this.select(this.storable$, (storable) => storable?.flattenedSecurityMap);
	readonly flatMenuItems$ = this.select(this.storable$, (storable) => storable?.flatMenuItems);
	readonly settings$ = this.select(this.storable$, (storable) => storable?.settings);
	readonly lookups$ = this.select(this.storable$, (storable) => storable?.lookups || ({} as LookupRegistry));

	readonly topHost$ = this.select(({ topHost }) => topHost || '');
	readonly toolbarButtonsPortals$ = this.select(({ toolbarButtonsPortals }) => toolbarButtonsPortals || []);
	readonly toolbarButtonsPortalsRight$ = this.select(({ toolbarButtonsPortals }) =>
		(toolbarButtonsPortals || []).filter((p) => !p.positionLeft),
	);
	readonly toolbarButtonsPortalsLeft$ = this.select(({ toolbarButtonsPortals }) =>
		(toolbarButtonsPortals || []).filter((p) => p.positionLeft),
	);

	readonly isAuthenticated$ = this.select(this.jwt$, (jwt) => !!jwt);
	readonly staffName$ = this.select(this.settings$, (settings) => settings?.staffName);

	readonly mainSideNavOpen$ = this.select(({ sideNavOpen }) => !!sideNavOpen);
	readonly isHandset$ = this.select(({ isHandset }) => !!isHandset);

	readonly isCoarseTouchDevice$ = this.select(({ isCoarseTouchDevice }) => !!isCoarseTouchDevice);

	private readonly securityConfigStack$ = this.select(({ storable }) => storable?.securityConfigStack || []);

	readonly allowSideNavAutoOpen$ = this.select(({ allowSideNavAutoOpen }) => !!allowSideNavAutoOpen);

	readonly redirectAfterLogonUrl$ = this.select(({ redirectAfterLogonUrl }) => redirectAfterLogonUrl ?? '');

	readonly collapseToolbarButtons$ = this.select(({ collapseToolbarButtons }) => !!collapseToolbarButtons);

	readonly isPaintingSecurity$ = this.select(({ isPaintingSecurity }) => !!isPaintingSecurity);
	readonly isPaintingSecurityForStaff$ = this.select(
		({ isPaintingSecurityForStaff }) => !!isPaintingSecurityForStaff,
	);
	readonly isPaintingWorkflowSecurity$ = this.select(
		({ isPaintingWorkflowSecurity }) => !!isPaintingWorkflowSecurity,
	);

	readonly vm$ = this.select({
		collapseToolbarButtons: this.collapseToolbarButtons$,
		isHandset: this.isHandset$,
		mainSideNavOpen: this.mainSideNavOpen$,
		isPaintingSecurity: this.isPaintingSecurity$,
		isPaintingWorkflowSecurity: this.isPaintingWorkflowSecurity$,
		staffName: this.staffName$,
	});

	/**
	 * Returns whether there's an authenticated session.
	 */
	get isAuthenticated() {
		return !!this.get().storable?.jwt;
	}

	get isPaintingSecurityOrWorkflowSecurity() {
		const state = this.get();
		return state.isPaintingSecurity || state.isPaintingWorkflowSecurity;
	}

	get isPaintingSecurity() {
		const state = this.get();
		return !!state.isPaintingSecurity;
	}

	/**
	 * Returns the default username to be used in the login form.
	 */
	get defaultUsername() {
		return this.localState?.defaultUsername || '';
	}

	/**
	 * Returns the current JWT token.
	 */
	get jwt() {
		return this.get().storable?.jwt;
	}

	/**
	 * Synchronously returns whether the device is a handset.
	 */
	get isHandset() {
		return !!this.get().isHandset;
	}

	get securityRouteOverride() {
		return this._securityRouteOverride;
	}

	set securityRouteOverride(value: string | undefined) {
		this._securityRouteOverride = value;
	}

	/**
	 * Gets the startup customisation data.
	 */
	get customisation() {
		return this.localState?.customisation;
	}

	get flattenedSecurityMap() {
		return this.get().storable?.flattenedSecurityMap;
	}

	get securityMap() {
		return this.get().storable?.securityMap;
	}

	get flatMenuItems() {
		return this.get().storable?.flatMenuItems;
	}

	get editorMap() {
		return this.get().storable?.editorMap;
	}

	/**
	 * Gets the system settings.
	 */
	get settings(): Settings {
		return this.get().storable?.settings ?? new Settings();
	}

	get locale() {
		return this.customisation?.cultureKey || 'en-GB';
	}

	get currencyCode() {
		return this.customisation?.currencyCode || 'GBP';
	}

	get startupParams(): StartupParams | undefined {
		return this.get().storable?.startupParams;
	}

	get topHost() {
		const result = this.get().topHost || '';
		return result;
	}

	get isSideNavOpen() {
		return !!this.get().sideNavOpen;
	}

	get isCoarseTouchDevice() {
		return !!this.get().isCoarseTouchDevice;
	}

	private get lookups() {
		return this.get().storable?.lookups || {};
	}

	constructor() {
		super({});

		AppStateService.instance = this;

		this.hydrateFromStorage();

		const touch = matchMedia('(hover: none), (pointer: coarse)').matches;
		this.patchState({
			isCoarseTouchDevice: touch,
		});

		this.setAllowSideNavAutoOpen(true);
		this.saveToSessionStorage();
	}

	readonly setRedirectAfterLogonUrl = this.effect((url$: Observable<string>) =>
		url$.pipe(
			tap((url) => {
				this.patchState({ redirectAfterLogonUrl: url });
			}),
		),
	);

	private readonly patchStorable = this.updater((state, storable: SessionStateStorable) => ({
		...state,
		storable: { ...state.storable, ...storable },
	}));

	readonly triggerRebuildMenus = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.rebuildMenusSubject.next();
			}),
		),
	);

	readonly setMessageCount = this.effect((messageCount$: Observable<number>) =>
		messageCount$.pipe(
			tap((messageCount) => {
				this.patchStorable({ messageCount });
				this.persistToStorage();
			}),
		),
	);

	readonly setAlertCount = this.effect((alertCount$: Observable<number>) =>
		alertCount$.pipe(
			tap((alertCount) => {
				this.patchStorable({ alertCount });
				this.persistToStorage();
			}),
		),
	);

	readonly registerLookups = this.effect((registration$: Observable<LookupsRegistration>) =>
		registration$.pipe(
			withLatestFrom(this.lookups$),
			tap(([registration, lookups]) => {
				registration.forEach((lookup) => {
					const entries = lookup.entries || [];
					lookups[lookup.typeName] = entries;
				});

				this.patchStorable({ lookups: { ...lookups } });
				this.afterSettingsUpdated();
			}),
		),
	);

	readonly updateLookups = this.effect((params$: Observable<{ typeName: LookupType; entries: LookupDto[] }>) =>
		params$.pipe(
			withLatestFrom(this.lookups$),
			tap(([params, lookups]) => {
				lookups[params.typeName] = params.entries;

				this.patchStorable({ lookups: { ...lookups } });
				this.afterSettingsUpdated();
			}),
		),
	);

	readonly setStartupParams = this.effect((startupParams$: Observable<StartupParams>) =>
		startupParams$.pipe(
			tap((params) => {
				this.patchStorable({ startupParams: params });

				// If starting up, and an iCARE session ID is provided, or being asked to reset a password, then clear the session.
				if (params.iCareSessionId || params.passwordResetKey) {
					this.setNotAuthenticated();
				}
			}),
		),
	);

	readonly clearStartupParams = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.patchStorable({ startupParams: undefined });
			}),
		),
	);

	readonly onAuthenticated = this.effect((data$: Observable<AuthenticationData>) =>
		data$.pipe(
			tap((data) => {
				const settings: Settings = new Settings(data.options);

				// parse the search and replace settings.
				this.parseSearchAndReplace(settings);

				const flattenedSecurityMap = buildFlattenedSecurityMap(data.securityMap as RawSecurityMap);

				const hashObj = {
					securityMap: data.securityMap,
					editorMap: data.editorMap,
				};

				const hash = this.simpleHash(JSON.stringify(hashObj));

				this.patchStorable({
					jwt: data.jwt,
					lastAccessTime: new Date(),
					messageCount: 0,
					alertCount: 0,
					securityMap: data.securityMap,
					editorMap: data.editorMap,
					flattenedSecurityMap: flattenedSecurityMap,
					flatMenuItems: data.flatMenuItems,
					settings,
					hash,
				});

				this.persistToStorage();
			}),
		),
	);

	getFlattenedSecurityMapForAccessCheck() {
		const stack = this.get().storable?.securityConfigStack || [];
		if (!stack.length) {
			return this.flattenedSecurityMap;
		}
		const bottomMap = stack[0].flattenedSecurityMap;
		return bottomMap;
	}

	// A simple, *insecure* 32-bit hash that's short, fast, and has no dependencies.
	// Output is always 7 characters.
	// Loosely based on the Java version; see
	// https://stackoverflow.com/questions/6122571/simple-non-secure-hash-function-for-javascript
	private simpleHash(str: string) {
		let hash = 0;
		for (let i = 0; i < str.length; i++) {
			const char = str.charCodeAt(i);
			hash = (hash << 5) - hash + char;
		}
		// Convert to 32bit unsigned integer in base 36 and pad with "0" to ensure length is 7.
		return (hash >>> 0).toString(36).padStart(7, '0');
	}

	readonly checkSessionSecurityHashAndResetSecurityIfInvalid = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.storable$),
			tap(([, storable]) => {
				if (!storable) {
					throw new Error('No session state found.');
				}

				const hashObj = {
					securityMap: storable.securityMap,
					editorMap: storable.editorMap,
				};

				const hash = this.simpleHash(JSON.stringify(hashObj));
				if (hash !== storable.hash) {
					this.patchStorable({
						...storable,
						flattenedSecurityMap: {},
					});
				}
			}),
		),
	);

	readonly pushSecurityConfig = this.effect((entries$: Observable<SecurityConfigStackEntry[]>) =>
		entries$.pipe(
			withLatestFrom(this.securityConfigStack$),
			tap(([entries, stack]) => {
				const [oldEntry, entry] = entries;
				stack.push(oldEntry);

				this.patchStorable({
					securityMap: entry.securityMap,
					flattenedSecurityMap: entry.flattenedSecurityMap,
					securityConfigStack: stack,
				});

				this.persistToStorage();
			}),
		),
	);

	readonly popSecurityConfig = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.securityConfigStack$),
			tap(([, stack]) => {
				if (!stack.length) {
					return;
				}

				const popped = stack.pop();
				this.patchStorable({
					securityMap: popped?.securityMap,
					editorMap: popped?.editorMap,
					flattenedSecurityMap: popped?.flattenedSecurityMap,
					securityConfigStack: stack,
				});

				this.persistToStorage();
			}),
		),
	);

	readonly clearPaintingSecurity = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.patchState({
					isPaintingSecurity: false,
					isPaintingSecurityForStaff: false,
				});
			}),
		),
	);

	readonly setIsPaintingSecurity = this.effect((params$: Observable<IsPaintingSecurityParams>) =>
		params$.pipe(
			tap((params) => {
				this.patchState({
					isPaintingSecurity: params.isPaintingSecurity,
					isPaintingSecurityForStaff: params.isPaintingSecurityForStaff,
				});
			}),
		),
	);

	readonly setIsPaintingWorkflowSecurity = this.effect((isPaintingWorkflowSecurity$: Observable<boolean>) =>
		isPaintingWorkflowSecurity$.pipe(
			tap((isPaintingWorkflowSecurity) => {
				this.patchState({ isPaintingWorkflowSecurity });
			}),
		),
	);

	readonly setMainSideNavOpen = this.effect((state$: Observable<boolean>) =>
		state$.pipe(
			tap((state) => {
				this.patchState({ sideNavOpen: state });
			}),
		),
	);

	readonly setCollapseToolbarButtons = this.updater((state, collapseToolbarButtons: boolean) => ({
		...state,
		collapseToolbarButtons,
	}));

	/**
	 * Determines whether the side nav should automatically open when the screen is large enough.
	 * Normally, this will happen, but on certain screens - e.g. items matrix, and rota screens,
	 * we want the side menu to remain closed.
	 */
	readonly setAllowSideNavAutoOpen = this.effect((allow$: Observable<boolean>) =>
		allow$.pipe(
			tap((allow) => {
				this.patchState({ allowSideNavAutoOpen: allow });
			}),
		),
	);

	readonly toggleMainSideNavOpen = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.mainSideNavOpen$),
			tap(([, open]) => {
				this.patchState({ sideNavOpen: !open });
			}),
		),
	);

	readonly setIsHandset = this.effect((isHandset$: Observable<boolean>) =>
		isHandset$.pipe(
			tap((isHandset) => {
				this.patchState({ isHandset });
			}),
		),
	);

	/**
	 * Updates the JWT token.
	 */
	readonly updateJwt = this.effect((jwt$: Observable<string>) =>
		jwt$.pipe(
			withLatestFrom(this.storable$),
			tap(([jwt, storable]) => {
				storable = storable || {};
				storable.jwt = jwt;

				this.persistToStorage();
			}),
		),
	);

	/**
	 * Clears the session.
	 */
	readonly setNotAuthenticated = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.updateJwt('');
				this.resetState();
			}),
		),
	);

	/**
	 * Records user activity as now.
	 */
	readonly recordLastAccessTime = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.patchStorable({ lastAccessTime: new Date() });
				this.persistToStorage();
			}),
		),
	);

	readonly afterSettingsUpdated = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.persistToStorage();
			}),
		),
	);

	readonly setStartupCustomisation = this.effect(
		(startupCustomisation$: Observable<StartupCustomisationDto | undefined>) =>
			startupCustomisation$.pipe(
				tap((customisation) => {
					this.localState = {
						...this.localState,
						customisation,
					};

					localisation.locale = this.locale;
					localisation.currencyCode = this.currencyCode;

					this.persistToStorage();
				}),
			),
	);

	readonly setTopHost = this.effect((topHost$: Observable<string>) =>
		topHost$.pipe(
			tap((topHost) => {
				this.patchState({ topHost });
			}),
		),
	);

	/**
	 * Updates the default username to be used in the login form.
	 * @param username
	 */
	readonly updateDefaultUsername = this.effect((username$: Observable<string | null>) =>
		username$.pipe(
			tap((username) => {
				this.localState = {
					...this.localState,
					defaultUsername: username,
				};

				this.persistToStorage();
			}),
		),
	);

	private readonly configureScreenToolbar = this.effect((config$: Observable<ToolbarConfig>) =>
		config$.pipe(
			withLatestFrom(this.screenToolbarConfigByHost$),
			tap(([config, byHost]) => {
				const host = config.host ?? '';
				const existing = byHost[host] || { host };
				byHost[host] = existing;
				existing.portal = config.portal;

				this.patchState({ screenToolbarConfigByHost: { ...byHost } });
			}),
		),
	);

	private readonly configureScreenName = this.effect((config$: Observable<ScreenNameConfig>) =>
		config$.pipe(
			withLatestFrom(this.screenToolbarConfigByHost$),
			tap(([config, byHost]) => {
				const host = config.host ?? '';
				const existing = byHost[host] || { host };
				byHost[host] = existing;
				existing.screenName = config.screenName;

				this.patchState({ screenToolbarConfigByHost: { ...byHost } });
			}),
		),
	);

	readonly destroyHost = this.effect((host$: Observable<string>) =>
		host$.pipe(
			withLatestFrom(this.screenToolbarConfigByHost$),
			tap(([hostName, byHost]) => {
				hostName ||= '';
				delete byHost[hostName];

				this.patchState({ screenToolbarConfigByHost: { ...byHost }, topHost: '' });
			}),
		),
	);

	readonly saveToSessionStorage = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.persistToStorage();
			}),
		),
	);

	readonly addButtonsToolbarPortal = this.effect((config$: Observable<ButtonsToolbarContentConfig>) =>
		config$.pipe(
			withLatestFrom(this.toolbarButtonsPortals$),
			tap(([config, current]) => {
				current = [...current, config];
				this.patchState({ toolbarButtonsPortals: current });
			}),
		),
	);

	readonly removeButtonsToolbarPortal = this.effect((portal$: Observable<TemplatePortal>) =>
		portal$.pipe(
			withLatestFrom(this.toolbarButtonsPortals$),
			tap(([portal, current]) => {
				current = current.filter((p) => p.portal !== portal);
				this.patchState({ toolbarButtonsPortals: current });
			}),
		),
	);

	private readonly resetState = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.updateJwt('');

				const state = this.get();

				this.setState({
					...defaultState,
					storable: {
						startupParams: state.storable?.startupParams,
					},
					isHandset: state.isHandset,
					isCoarseTouchDevice: state.isCoarseTouchDevice,
					allowSideNavAutoOpen: state.allowSideNavAutoOpen,
				});
				this.persistToStorage();
			}),
		),
	);

	/**
	 * Gets an Observable of the screen name for a specific host.
	 * @param host
	 * @returns
	 */
	getScreenName(host: string) {
		return this.screenToolbarConfigByHost$.pipe(
			map((byHost) => {
				const screenName = byHost[host]?.screenName || '';
				return screenName;
			}),
		);
	}

	/**
	 * Gets an Observable of the screen toolbar portal for a specific host.
	 * @param host
	 * @returns
	 */
	getToolbarPortal(host: string) {
		return this.screenToolbarConfigByHost$.pipe(
			map((byHost) => {
				const portal = byHost[host]?.portal;
				return portal;
			}),
		);
	}

	getToolbarButtonsPortalLeft(host: string) {
		return this.toolbarButtonsPortalsLeft$.pipe(
			map((portals) => {
				return portals.filter((p) => p.host === host);
			}),
		);
	}

	getToolbarButtonsPortalRight(host: string) {
		return this.toolbarButtonsPortalsRight$.pipe(
			map((portals) => {
				return portals.filter((p) => p.host === host);
			}),
		);
	}

	/**
	 * Gets an observable for a lookup collection.
	 * @param typeName The type name of the lookup collection.
	 * @returns
	 */
	getLookupCollection<TLookup extends LookupDto = LookupDto>(typeName: LookupType): Observable<TLookup[]> {
		return this.lookups$.pipe(
			switchMap((lookups) => {
				return of(lookups[typeName] || []).pipe(map((values) => values as TLookup[]));
			}),
		);
	}

	/**
	 * Synchronously returns an already loaded lookup collection.
	 * @param typeName
	 * @returns
	 */
	getRealisedLookupCollection(typeName: string): LookupDto[] {
		const lookups = this.get().storable?.lookups;
		return lookups?.[typeName] || [];
	}

	/**
	 * Refreshes the lookups collection for a single type name.
	 * @param typeName
	 * @param entries
	 */
	private refreshLookups(typeName: string, entries: LookupDto[]) {
		this.registerLookups([{ typeName, entries }]);
	}

	/**
	 * Replaces an existing Lookup in a collection with a replacement.
	 * @param typeName
	 * @param replacement
	 */
	replaceLookup(typeName: LookupType, replacement: LookupDto) {
		const lookups = this.lookups;

		let coll = this.getRealisedLookupCollection(typeName);
		const index = coll.findIndex((lookup) => lookup.oid === replacement.oid);
		if (index >= 0) {
			coll = [...coll.slice(0, index), replacement, ...coll.slice(index + 1)];
		} else {
			coll = [...coll, replacement];
		}

		lookups[typeName] = [...coll];

		this.patchStorable({ lookups });
		this.persistToStorage();
	}

	/**
	 * Sets the portal for the buttons to appear on a toolbar, determined by the host.
	 * A blank host will render the toolbar portal on the main toolbar.
	 *
	 * @param portal The TemplatePortal containing the toolbar content to render.
	 * @param host Determines where the buttons are rendered. If undefined, they'll appear on the main toolbar.
	 */
	setScreenToolbarPortal(opts: { portal: TemplatePortal | undefined; host?: string; noSpacer?: boolean }) {
		opts.host ||= '';
		opts.noSpacer ??= false;

		this.configureScreenToolbar({ portal: opts.portal, host: opts.host, noSpacer: opts.noSpacer });
	}

	/**
	 * Sets the screen name to appear in a toolbar, determined by the host.
	 * A blank host will render the screen name on the main toolbar.
	 *
	 * @param name The screen name to display.
	 * @param host The optional host to display the screen name on.
	 */
	setScreenName(name: string, host = '') {
		this.configureScreenName({ screenName: name, host });
	}

	getState() {
		return this.get();
	}

	private hydrateFromStorage() {
		try {
			const state = JSON.parse(sessionStorage.getItem(SessionStorage_Key) ?? '{}');
			this.patchState({ storable: state });
		} catch {
			// Ignore error
			this.setState({});
		}

		try {
			this.localState = JSON.parse(localStorage.getItem(LocalStorage_Key) ?? '{}');
		} catch {
			// Ignore error
			this.localState = {
				defaultUsername: null,
				customisation: undefined,
			};
		}

		localisation.locale = this.locale;
		localisation.currencyCode = this.currencyCode;

		// Re-parse the search and replace settings.
		this.parseSearchAndReplace(this.settings);
	}

	/**
	 * Converts the search and replace string into an array of SearchAndReplace objects, which turn the search
	 * string into a RegExp.
	 */
	private parseSearchAndReplace(settings: Settings): void {
		const sar = settings.sar || '';
		const result = [] as SearchAndReplace[];

		const parts = sar.split('|');
		for (let i = 0; i < parts.length; i += 2) {
			const search = parts[i];
			const replace = parts[i + 1];

			if (!search || !replace) {
				continue;
			}
			result.push({ search: new RegExp(search, 'ig'), replace });
		}

		settings.parsedSearchAndReplace = result;
	}

	private persistToStorage() {
		const state = this.get().storable || {};
		const hasKeys = Object.keys(state).length > 0;

		if (hasKeys) {
			this.sessionStateCleanup.writeItemToSessionStorageWithRetry(SessionStorage_Key, JSON.stringify(state));
		} else {
			sessionStorage.removeItem(SessionStorage_Key);
		}

		if (this.localState) {
			localStorage.setItem(LocalStorage_Key, JSON.stringify(this.localState));
		} else {
			localStorage.removeItem(LocalStorage_Key);
		}
	}
}
